import React, { useState, useEffect } from "react";
import { Edit } from "@refinedev/antd";
import { useOne, useCustomMutation } from "@refinedev/core";
import { message, Form, Input, InputNumber, Select, Card, Row, Col, Descriptions, Alert, Space, Button, DatePicker } from "antd";
import dayjs from "dayjs";
import { PAYMENT_REQUEST_BY_ID_QUERY } from "./queries";
import { PaymentRequest, PaymentRequestPriority, Currency, PaymentRequestStatus, Attachment } from "./interface";
import { UPDATE_PAYMENT_REQUEST_MUTATION } from "./queries";

export const EditPaymentRequest: React.FC = () => {
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const queryResult = useOne<PaymentRequest>({
    resource: "paymentRequests",
    id: window.location.pathname.split("/").pop() || "",
    meta: {
      gqlQuery: PAYMENT_REQUEST_BY_ID_QUERY,
    },
  });

  const { data, isLoading } = queryResult;
  const record = data?.data;

  const { mutate: updateMutate, isLoading: updating } = useCustomMutation();

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        invoiceId: record.invoiceId,
        poNo: record.poNo,
        receivingNo: record.receivingNo,
        supplierId: record.supplierId,
        supplierName: record.supplierName,
        amount: record.amount,
        currency: record.currency,
        exchangeRate: record.exchangeRate,
        dueDate: record.dueDate ? dayjs(record.dueDate) : undefined,
        priority: record.priority,
        bankAccountDetails: record.bankAccountDetails,
        notes: record.notes,
      });
      setAttachments(record.attachments || []);
    }
  }, [record, form]);

  const handleFileUpload = async (file: File) => {
    // This is a placeholder for file upload functionality
    // In a real implementation, you would upload the file to file storage service
    // and get back a fileId
    const newAttachment: Attachment = {
      fileId: `temp-${Date.now()}`,
      originalName: file.name,
      mimeType: file.type,
      size: file.size,
      category: "document",
    };
    setAttachments([...attachments, newAttachment]);
    return false; // Prevent default upload behavior
  };

  const handleRemoveAttachment = (fileId: string) => {
    setAttachments(attachments.filter((att) => att.fileId !== fileId));
  };

  const onUpdate = async (values: any) => {
    if (!record) return;

    updateMutate(
      {
        url: "",
        method: "post",
        values: {
          id: record.id,
          input: {
            ...values,
            dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
            attachments,
          },
        },
        meta: { gqlMutation: UPDATE_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request updated successfully");
          window.location.href = `/payment-requests/show/${record.id}`;
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to update payment request");
        },
      },
    );
  };

  const isEditable = record?.status === PaymentRequestStatus.DRAFT;

  return (
    <Edit
      title={`Edit Payment Request ${record?.invoiceId || ""}`}
      saveButtonProps={{ loading: updating, style: { display: "none" } }}
      footerButtons={() => null}
      isLoading={isLoading}
    >
      {record && (
        <Form form={form} layout="vertical" onFinish={onUpdate}>
          <Card title="Basic Information (Read Only)" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Invoice ID">{record.invoiceId}</Descriptions.Item>
              <Descriptions.Item label="PO Number">{record.poNo}</Descriptions.Item>
              <Descriptions.Item label="Receiving Number">{record.receivingNo}</Descriptions.Item>
              <Descriptions.Item label="Supplier ID">{record.supplierId}</Descriptions.Item>
              <Descriptions.Item label="Supplier Name" span={2}>{record.supplierName}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Payment Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Amount"
                  name="amount"
                  rules={[{ required: true, message: "Amount is required" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.01}
                    placeholder="Enter amount"
                    disabled={!isEditable}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Currency"
                  name="currency"
                  rules={[{ required: true, message: "Currency is required" }]}
                >
                  <Select placeholder="Select currency" disabled={!isEditable}>
                    {Object.values(Currency).map((currency) => (
                      <Select.Option key={currency} value={currency}>
                        {currency}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Exchange Rate"
                  name="exchangeRate"
                  rules={[{ required: true, message: "Exchange rate is required" }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.01}
                    placeholder="Enter exchange rate"
                    disabled={!isEditable}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Due Date"
                  name="dueDate"
                  rules={[{ required: true, message: "Due date is required" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={!isEditable}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Priority"
              name="priority"
              rules={[{ required: true, message: "Priority is required" }]}
            >
              <Select placeholder="Select priority" disabled={!isEditable}>
                {Object.values(PaymentRequestPriority).map((priority) => (
                  <Select.Option key={priority} value={priority}>
                    {priority}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Bank Name"
              name={["bankAccountDetails", "bank"]}
              rules={[{ required: true, message: "Bank name is required" }]}
            >
              <Input placeholder="Enter bank name" disabled={!isEditable} />
            </Form.Item>

            <Form.Item
              label="Account Number"
              name={["bankAccountDetails", "accountNo"]}
              rules={[{ required: true, message: "Account number is required" }]}
            >
              <Input placeholder="Enter account number" disabled={!isEditable} />
            </Form.Item>

            <Form.Item label="Notes" name="notes">
              <Input.TextArea rows={3} placeholder="Optional notes" disabled={!isEditable} />
            </Form.Item>
          </Card>

          <Card title="Attachments" style={{ marginBottom: 16 }}>
            <Form.Item label="Upload Documents">
              <Space direction="vertical" style={{ width: "100%" }}>
                {isEditable && (
                  <>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach(handleFileUpload);
                      }}
                      style={{ display: "none" }}
                      id="file-upload"
                    />
                    <Button onClick={() => document.getElementById("file-upload")?.click()}>
                      Upload Files
                    </Button>
                  </>
                )}
                {attachments.length > 0 && (
                  <div>
                    {attachments.map((att) => (
                      <div key={att.fileId} style={{ marginBottom: 8 }}>
                        <Space>
                          <span>{att.originalName}</span>
                          {isEditable && (
                            <Button
                              type="link"
                              danger
                              size="small"
                              onClick={() => handleRemoveAttachment(att.fileId)}
                            >
                              Remove
                            </Button>
                          )}
                        </Space>
                      </div>
                    ))}
                  </div>
                )}
                {!isEditable && attachments.length === 0 && (
                  <Alert message="No attachments" type="info" />
                )}
              </Space>
            </Form.Item>
          </Card>

          {record.status === PaymentRequestStatus.COMPLETED && record.payment && (
            <Card title="Payment Information (Read Only)" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Voucher Number">{record.payment.voucherNumber || "-"}</Descriptions.Item>
                <Descriptions.Item label="Paid Amount">
                  {record.currency} {record.payment.paidAmount || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Date">
                  {record.payment.paymentDate ? dayjs(record.payment.paymentDate).format("YYYY-MM-DD") : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method">{record.payment.method || "-"}</Descriptions.Item>
                <Descriptions.Item label="Reference" span={2}>{record.payment.reference || "-"}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Card>
            <Space>
              <Button type="default" onClick={() => window.location.href = `/payment-requests/show/${record.id}`}>
                Cancel
              </Button>
              {isEditable && (
                <Button type="primary" htmlType="submit" loading={updating}>
                  Save Changes
                </Button>
              )}
            </Space>
          </Card>
        </Form>
      )}
    </Edit>
  );
};
